import React, { useEffect, useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Dialog, DialogPortal, DialogOverlay, DialogHeader, DialogTitle, cn } from '@ohif/ui-next';
import {
  SIGNATURE_PREVIEW_KIND,
  inferMimeTypeHintFromSignatureFileName,
  getSignatureFilePreviewKind,
} from '../constants';

export interface SignaturePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceUrl: string | null;
  /** When true, sourceUrl is a blob: URL from the parent (local file). */
  sourceUrlOwnedByParent?: boolean;
  fileName?: string | null;
  mimeTypeHint?: string | null;
}

const SignaturePreviewDialog: React.FC<SignaturePreviewDialogProps> = ({
  open,
  onOpenChange,
  sourceUrl,
  sourceUrlOwnedByParent = false,
  fileName,
  mimeTypeHint,
}) => {
  const [embedFailed, setEmbedFailed] = useState(false);

  const preview = useMemo(() => {
    if (!open || !sourceUrl) {
      return null;
    }
    if (sourceUrlOwnedByParent) {
      return {
        url: sourceUrl,
        mime: mimeTypeHint || '',
        isDirectRemote: false,
      };
    }
    if (/^https?:\/\//i.test(sourceUrl)) {
      return {
        url: sourceUrl,
        mime: (mimeTypeHint || inferMimeTypeHintFromSignatureFileName(fileName)).trim(),
        isDirectRemote: true,
      };
    }
    return {
      url: sourceUrl,
      mime: mimeTypeHint || inferMimeTypeHintFromSignatureFileName(fileName),
      isDirectRemote: false,
    };
  }, [open, sourceUrl, sourceUrlOwnedByParent, mimeTypeHint, fileName]);

  useEffect(() => {
    setEmbedFailed(false);
  }, [preview?.url]);

  const previewKind = preview
    ? getSignatureFilePreviewKind(preview.mime, fileName)
    : SIGNATURE_PREVIEW_KIND.OTHER;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogPortal>
        <DialogOverlay className="z-[1100]" />
        <DialogPrimitive.Content
          className={cn(
            'signature-preview-dialog-content',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'fixed left-1/2 top-1/2 z-[1101] flex -translate-x-1/2 -translate-y-1/2 flex-col bg-white text-[#0f172a] duration-200'
          )}
        >
          <DialogHeader className="signature-preview-dialog-header">
            <DialogTitle>Signature preview</DialogTitle>
            {fileName ? <p className="signature-preview-dialog-filename">{fileName}</p> : null}
          </DialogHeader>
          <div className="signature-preview-dialog-body">
            {preview && previewKind === SIGNATURE_PREVIEW_KIND.PDF && (
              <>
                <iframe
                  title="Signature PDF preview"
                  src={preview.url}
                  className="signature-preview-dialog-frame"
                />
                {preview.isDirectRemote && (
                  <p className="signature-preview-dialog-remote-hint">
                    <a
                      href={preview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="signature-preview-dialog-open-tab"
                    >
                      Open PDF in new tab
                    </a>{' '}
                    if the preview stays blank.
                  </p>
                )}
              </>
            )}
            {preview && previewKind === SIGNATURE_PREVIEW_KIND.IMAGE && !embedFailed && (
              <img
                src={preview.url}
                alt=""
                className="signature-preview-dialog-image"
                onError={() => setEmbedFailed(true)}
              />
            )}
            {preview && previewKind === SIGNATURE_PREVIEW_KIND.IMAGE && embedFailed && (
              <div className="signature-preview-dialog-fallback">
                <p className="signature-preview-dialog-status">
                  Embedded preview failed (file may require authentication). Open the file directly.
                </p>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="signature-preview-dialog-open-tab"
                >
                  Open image in new tab
                </a>
              </div>
            )}
            {preview && previewKind === SIGNATURE_PREVIEW_KIND.OTHER && (
              <div className="signature-preview-dialog-fallback">
                <p className="signature-preview-dialog-status">
                  Preview is not available for this file type.
                </p>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="signature-preview-dialog-open-tab"
                >
                  Open in new tab
                </a>
              </div>
            )}
          </div>
          <DialogPrimitive.Close
            type="button"
            aria-label="Close"
            className="signature-preview-dialog-close"
          >
            <Cross2Icon
              className="h-4 w-4"
              aria-hidden
            />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default SignaturePreviewDialog;
